/**
 * Route guard for the test-clinic journey (ADORACARE-VISIT-CASE-SPINE-V1;
 * gated by TEST-SPINE-GATING-V1). Mirrors RequireLeadCockpit: the pure
 * decision lives in decideTestJourneyAccess (journey-view.ts). Under GATING
 * ON (the seeded default) the decision keys off the server-computed /me door
 * signals (testJourneyEnabled / testJourneyGated -- the org-config test/QA
 * role set + designated-test-org pin, computed server-side); a refused
 * caller renders the HONEST 403 card (the existing DashboardLoadError
 * access-restricted primitive -- no second 403 component), never a blank
 * and never a silent redirect. Under GATING OFF (the per-org rollback row)
 * the legacy role decision applies exactly: demo drivers render, any other
 * role redirects to its own homePath.
 *
 * Backend route auth (the TEST-SPINE-GATING-V1 surface gate on BOTH
 * /api/journey routes, the staff read gate, the PATIENT_MASTER_CREATE_ROLES
 * link gate, and every composed writer's own gate) remains the real
 * security boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideTestJourneyAccess } from '@/dashboard/journey/journey-view';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireTestJourneyViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireTestJourneyView({
  loading,
  error,
  me,
  children,
}: RequireTestJourneyViewProps): JSX.Element | null {
  const decision = decideTestJourneyAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'deny':
      // TEST-SPINE-GATING-V1: the clean 403 page -- THE existing
      // status-driven access-restricted card (DashboardLoadError), honest
      // copy, no blank, no crash, no second 403 primitive.
      return (
        <div className="clinic-table" data-testid="test-journey-denied">
          <DashboardLoadError
            message="The test journey is restricted to the designated test organization and its QA roles."
            status={403}
          />
        </div>
      );
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireTestJourney(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireTestJourneyView loading={ctx.loading} error={ctx.error} me={ctx.me} />
  );
}
